import { useState } from "react";
import { api } from "@/App";
import StaffLayout from "@/components/layout/StaffLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Ticket, Search, CheckCircle, XCircle, Clock, User, Loader2 } from "lucide-react";
import { toast } from "sonner";

const StaffVoucherValidation = () => {
  const [code, setCode] = useState("");
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);
  const [markingUsed, setMarkingUsed] = useState(false);

  const handleValidate = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      toast.error("Please enter a voucher code");
      return;
    }

    setSearching(true);
    setResult(null);
    try {
      const response = await api.post(`/staff/vouchers/validate?code=${code.toUpperCase()}`);
      setResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Voucher not found");
    } finally {
      setSearching(false);
    }
  };

  const handleMarkUsed = async () => {
    if (!result?.voucher?.id) return;
    
    setMarkingUsed(true);
    try {
      await api.post(`/staff/vouchers/${result.voucher.id}/use`);
      toast.success("Voucher marked as used");
      setResult({ ...result, voucher: { ...result.voucher, status: 'used' }, is_valid: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark voucher as used");
    } finally {
      setMarkingUsed(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <StaffLayout title="Voucher Validation">
      <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
        {/* Search Form */}
        <Card className="bg-[#131316] border-[#2A2A2F]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Ticket className="text-[#D4AF37]" size={20} />
              Validate Voucher
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleValidate} className="flex gap-3">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="Enter voucher code (e.g., OR-XXXXXXXX)"
                className="flex-1 bg-[#1A1A1E] border-[#2A2A2F] text-white font-mono text-lg tracking-wider focus:border-[#D4AF37]"
                data-testid="voucher-code-input"
              />
              <Button 
                type="submit" 
                disabled={searching}
                className="bg-[#D4AF37] text-[#0A0A0B] hover:bg-[#B8942D]"
                data-testid="validate-voucher-btn"
              >
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search size={18} />}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Result */}
        {result && (
          <Card className={`border-2 ${
            result.is_valid ? 'bg-[#4ADE80]/5 border-[#4ADE80]' : 'bg-[#EF4444]/5 border-[#EF4444]'
          }`}>
            <CardContent className="p-6">
              {/* Status Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  {result.is_valid ? (
                    <div className="w-12 h-12 rounded-full bg-[#4ADE80]/20 flex items-center justify-center">
                      <CheckCircle className="text-[#4ADE80]" size={24} />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[#EF4444]/20 flex items-center justify-center">
                      <XCircle className="text-[#EF4444]" size={24} />
                    </div>
                  )}
                  <div>
                    <p className={`text-lg font-semibold ${result.is_valid ? 'text-[#4ADE80]' : 'text-[#EF4444]'}`}>
                      {result.validation_message}
                    </p>
                    <p className="text-[#6B6B6B] text-sm">Status: {result.voucher?.status}</p>
                  </div>
                </div>
              </div>

              {/* Voucher Details */}
              <div className="space-y-4">
                <div className="bg-[#0A0A0B] rounded-xl p-4">
                  <p className="text-[#6B6B6B] text-sm mb-1">Voucher Code</p>
                  <p className="text-white font-mono text-xl tracking-wider">{result.voucher?.code}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#0A0A0B] rounded-xl p-4">
                    <p className="text-[#6B6B6B] text-sm mb-1">Reward</p>
                    <p className="text-white font-medium">{result.reward?.name || 'Unknown'}</p>
                  </div>
                  <div className="bg-[#0A0A0B] rounded-xl p-4">
                    <p className="text-[#6B6B6B] text-sm mb-1">Value</p>
                    <p className="text-[#D4AF37] font-medium">
                      {result.reward?.value_cents ? `$${(result.reward.value_cents / 100).toFixed(2)}` : 'N/A'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#0A0A0B] rounded-xl p-4">
                    <p className="text-[#6B6B6B] text-sm mb-1">Issued</p>
                    <p className="text-white">{formatDate(result.voucher?.issued_at)}</p>
                  </div>
                  <div className="bg-[#0A0A0B] rounded-xl p-4">
                    <p className="text-[#6B6B6B] text-sm mb-1">Expires</p>
                    <p className="text-white">{formatDate(result.voucher?.expires_at)}</p>
                  </div>
                </div>

                {/* Owner Info */}
                {result.owner && (
                  <div className="bg-[#0A0A0B] rounded-xl p-4">
                    <p className="text-[#6B6B6B] text-sm mb-2">Owner</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#1A1A1E] flex items-center justify-center">
                        <User size={18} className="text-[#D4AF37]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{result.owner.name}</p>
                        <p className="text-[#6B6B6B] text-sm">{result.owner.email}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Mark as Used Button */}
                {result.is_valid && result.voucher?.status === 'active' && (
                  <Button
                    onClick={handleMarkUsed}
                    disabled={markingUsed}
                    className="w-full bg-[#4ADE80] text-[#0A0A0B] hover:bg-[#4ADE80]/90 font-semibold h-12"
                    data-testid="mark-used-btn"
                  >
                    {markingUsed ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Marking as Used...</>
                    ) : (
                      <><CheckCircle className="mr-2" size={18} /> Mark as Used</>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </StaffLayout>
  );
};

export default StaffVoucherValidation;
